import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ReleasePayoutModal from '@/components/modals/ReleasePayoutModal';
import HoldPayoutModal from '@/components/modals/HoldPayoutModal';
import { AlertCircle, X, Flag, Link as LinkIcon, Copy, ChevronLeft, CheckCircle, Download, ArrowRight, Check } from 'lucide-react';

interface Deduction {
  id: string;
  description: string;
  category: string;
  amount: number;
  authorisation: string;
  authorisedBy: string;
  date: string;
}

interface PayoutData {
  id: string;
  payoutId: string;
  providerName: string;
  status: 'pending' | 'approved' | 'processing' | 'released' | 'held' | 'disputed';
  netAmount: number;
  grossTotal: number;
  totalFees: number;
  platformFeePercentage: number;
  totalUrkOrders: number;
  totalUrkAmount: number;
  calculatedDate: string;
  legalName: string;
  accountType: string;
  taxId: string;
  bankName: string;
  bankAccount: string;
  bankAccountLast4: string;
  isVerified: boolean;
  auditTrailInfo: string;
  disputes: string[];
  batchPeriod?: string;
  approvalStatus?: string;
  deductions?: Deduction[];
}

const AdminPayoutDetailPage = () => {
  const { payoutId } = useParams<{ payoutId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('summary');
  const [holdReason, setHoldReason] = useState('');
  const [holdNotes, setHoldNotes] = useState('');
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  const [showReleasePayoutModal, setShowReleasePayoutModal] = useState(false);
  const [showHoldPayoutModal, setShowHoldPayoutModal] = useState(false);
  const [payoutData, setPayoutData] = useState<PayoutData | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch payout data from database
  useEffect(() => {
    const fetchPayoutData = async () => {
      try {
        const response = await apiService.request(`/admin/payouts/${payoutId}`, 'GET');
        if (response.success && response.data) {
          setPayoutData(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch payout data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (payoutId) {
      fetchPayoutData();
    }
  }, [payoutId]);

  // Handler: Release payout
  const handleReleasePayout = async () => {
    try {
      const response = await apiService.request(`/admin/payouts/${payoutId}/release`, 'POST', {
        status: 'RELEASED',
        releasedBy: localStorage.getItem('adminId'),
        releasedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Payout released successfully');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Release payout error:', error);
      alert('Failed to release payout: ' + error.message);
    }
  };

  // Handler: Hold payout
  const handleHoldPayout = async () => {
    try {
      const response = await apiService.request(`/admin/payouts/${payoutId}/hold`, 'POST', {
        status: 'HELD',
        holdReason: holdReason,
        holdNotes: holdNotes,
        heldBy: localStorage.getItem('adminId'),
        heldAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Payout held successfully');
        setShowHoldPayoutModal(false);
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Hold payout error:', error);
      alert('Failed to hold payout: ' + error.message);
    }
  };

  // Handler: Download payout details
  const handleDownloadPayout = async () => {
    try {
      const response = await apiService.request(`/admin/payouts/${payoutId}/export`, 'GET');
      if (response.success) {
        // Create CSV/PDF export
        const element = document.createElement('a');
        element.href = response.downloadUrl;
        element.download = `payout-${payoutId}.pdf`;
        element.click();
        alert('✓ Payout downloaded successfully');
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Download payout error:', error);
      alert('Failed to download payout: ' + error.message);
    }
  };

  // Use fetched data; show error if not available
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin">
            <CheckCircle className="text-blue-600" size={32} />
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!payoutData) {
    return (
      <AdminLayout>
        <div className="max-w-4xl mx-auto">
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <AlertCircle className="text-red-600" size={24} />
                <h3 className="text-lg font-bold text-red-700">Payout Not Found</h3>
              </div>
              <p className="text-sm text-red-600 mb-4">
                Unable to fetch payout data for ID: {payoutId}. Please check if the payout exists and try again.
              </p>
              <Button onClick={() => navigate('/admin/payouts')}>Back to Payouts</Button>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  const payout: PayoutData = payoutData;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return { badge: 'bg-teal-100 text-teal-800', dot: 'bg-teal-500' };
      default:
        return { badge: 'bg-gray-100 text-gray-800', dot: 'bg-gray-500' };
    }
  };

  const statusColor = getStatusColor(payout.status);

  return (
    <>
      <Helmet>
        <title>Payout Details - {payout.payoutId} - VireWire Admin</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen">
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-8 py-6">
              {activeTab === 'history' ? (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <button onClick={() => navigate('/admin/payouts')} className="flex items-center gap-2 text-teal-600 font-bold text-sm hover:text-teal-700">
                      <ChevronLeft className="w-4 h-4" />
                      Back to Payouts
                    </button>
                  </div>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-2xl font-bold text-gray-900 mb-1">{payout.payoutId}</p>
                      <p className="text-gray-600 font-medium">Status History & Audit Trail</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className="bg-teal-100 text-teal-700 px-3 py-1">
                        <span className="w-2 h-2 bg-teal-500 rounded-full inline-block mr-2"></span>
                        RELEASED
                      </Badge>
                      <button onClick={handleDownloadPayout} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white font-bold rounded-lg hover:bg-teal-700 transition-colors">
                        <Download className="w-4 h-4" />
                        Download Report
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-4 mb-4">
                    <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">Transaction Record</p>
                    <p className="text-sm font-bold text-teal-600">{payout.payoutId}</p>
                  </div>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h1 className="text-4xl font-bold text-gray-900 mb-1">Payout Review: {payout.providerName}</h1>
                      <p className="text-gray-600 font-medium">{payout.batchPeriod}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button onClick={handleDownloadPayout} className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors">
                        <Download className="w-4 h-4" />
                        Download CSV
                      </button>
                      <Button 
                        variant="outline"
                        className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                        onClick={handleHoldPayout}
                      >
                        ⏸ Hold Payout
                      </Button>
                      <Button 
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={handleReleasePayout}
                      >
                        ✓ Release Payout
                      </Button>
                      <button onClick={handleReleasePayout} className="px-6 py-2 bg-teal-600 text-white font-bold rounded-lg hover:bg-teal-700 transition-colors">
                        Confirm Payout
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="p-8">
            <div className="max-w-7xl mx-auto">
              {/* Metrics */}
              {activeTab !== 'history' && (
                <>
                  {activeTab === 'deductions' && (
                    <div className="grid grid-cols-3 gap-6 mb-8">
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Total Deductions</p>
                        <p className="text-4xl font-bold text-red-600">-${Math.abs(payout.totalFees).toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Platform Fee Rate</p>
                        <p className="text-4xl font-bold text-gray-900">{payout.platformFeePercentage}.0%</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Pending Disputes</p>
                        <p className="text-4xl font-bold text-gray-900">{payout.disputes.length}</p>
                      </div>
                    </div>
                  )}

                  {activeTab !== 'deductions' && (
                    <div className="grid grid-cols-3 gap-6 mb-8">
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Net Amount</p>
                        <p className="text-4xl font-bold text-gray-900">£{payout.netAmount.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Gross Total</p>
                        <p className="text-2xl font-bold text-gray-900">£{payout.grossTotal.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Total Fees</p>
                        <p className="text-2xl font-bold text-red-600">£{Math.abs(payout.totalFees).toFixed(2)}</p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Main Grid */}
              <div className="grid grid-cols-3 gap-8">
                {/* Left Column */}
                <div className="col-span-2">
                  {/* Tabs */}
                  <div className="mb-8">
                    <div className="border-b border-gray-200 flex items-center gap-8">
                      {['summary', 'ubbBreakdown', 'deductions', 'history'].map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveTab(tab)}
                          className={`pb-4 font-bold text-sm uppercase tracking-widest relative ${
                            activeTab === tab
                              ? 'text-gray-900 border-b-2 border-gray-900'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          {tab === 'summary' && 'Summary'}
                          {tab === 'ubbBreakdown' && 'Job Breakdown'}
                          {tab === 'deductions' && 'Deductions'}
                          {tab === 'history' && 'History'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Job Breakdown Tab Content */}
                  {activeTab === 'ubbBreakdown' && (
                    <>
                      {/* Job Breakdown Table */}
                      {(!payout.totalUrkOrders || payout.totalUrkOrders === 0) ? (
                        <Card className="mb-8">
                          <CardContent className="pt-8 pb-8">
                            <p className="text-center text-gray-500">No job breakdown data available</p>
                          </CardContent>
                        </Card>
                      ) : (
                        <Card className="mb-8">
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b border-gray-200 bg-gray-50">
                                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">ID</th>
                                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Service</th>
                                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Completed Date</th>
                                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Gross</th>
                                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Net</th>
                                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Array.from({ length: payout.totalUrkOrders }).map((_, idx) => (
                                  <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4"><span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-teal-100 text-teal-600 font-bold text-xs">{idx + 1}</span></td>
                                    <td className="px-6 py-4 font-bold text-gray-900">—</td>
                                    <td className="px-6 py-4 text-gray-600">—</td>
                                    <td className="px-6 py-4 font-bold text-gray-900">—</td>
                                    <td className="px-6 py-4 font-bold text-gray-900">—</td>
                                    <td className="px-6 py-4"><span className="inline-flex items-center gap-2"><span className="w-2 h-2 bg-teal-500 rounded-full"></span><span className="text-sm font-bold text-gray-900">Paid</span></span></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </Card>
                      )}

                      {/* Attachment Analysis */}
                      <div className="grid grid-cols-2 gap-6 mb-8">
                        <Card>
                          <CardContent className="pt-6">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <span className="text-xl">✓</span>
                              </div>
                              <div>
                                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">ATTACHMENT ANALYSIS</p>
                                <h3 className="text-lg font-bold text-gray-900 mb-2">All {payout.totalUrkOrders} Invoices Verified</h3>
                                <p className="text-sm text-gray-600">Cryptographically signed by {payout.providerName}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card className="bg-teal-50 border-teal-200">
                          <CardContent className="pt-6">
                            <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">TOTAL JOBS</p>
                            <div className="bg-teal-600 text-white rounded-lg p-6 text-center">
                              <p className="text-4xl font-bold">{String(payout.totalUrkOrders).padStart(2, '0')}</p>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </>
                  )}

                  {/* Deductions Tab Content */}
                  {activeTab === 'deductions' && (
                    <Card className="mb-8">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Description & Category</th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Amount</th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Authorisation</th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payout.deductions?.map((deduction) => (
                              <tr key={deduction.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4">
                                  <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-teal-100 rounded flex items-center justify-center text-teal-600 font-bold text-sm flex-shrink-0 mt-0.5">%</div>
                                    <div>
                                      <p className="font-bold text-gray-900">{deduction.description}</p>
                                      <p className="text-xs text-gray-600 mt-1">{deduction.category}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <p className="font-bold text-red-600">-${Math.abs(deduction.amount).toFixed(2)}</p>
                                </td>
                                <td className="px-6 py-4">
                                  <div>
                                    <p className="text-xs font-bold text-gray-900 uppercase tracking-widest">{deduction.authorisation}</p>
                                    <p className="text-xs text-gray-600 mt-0.5">{deduction.authorisedBy}</p>
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600">{deduction.date}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  )}

                  {/* History Tab Content */}
                  {activeTab === 'history' && (
                    <div className="space-y-6">
                      {/* Timeline */}
                      <div className="relative">
                        {payout.status === 'released' && (
                          <>
                            {/* Released Event */}
                            <div className="flex gap-6 pb-8 border-l-2 border-gray-300 pl-8 ml-6 relative">
                              <div className="absolute left-0 top-0 w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center -ml-7 border-4 border-white">
                                <CheckCircle className="w-6 h-6 text-teal-600" />
                              </div>
                              <div className="flex-1">
                                <div className="bg-white p-6 rounded-lg border border-gray-200">
                                  <h3 className="text-lg font-bold text-gray-900 mb-2">Released</h3>
                                  <p className="text-sm text-gray-600 mb-4">Funds have been successfully dispatched to the recipient's bank account.</p>
                                  <div className="flex items-center gap-4 text-xs text-gray-600">
                                    <span>{payout.calculatedDate || '—'}</span>
                                    <span className="flex items-center gap-2 font-bold text-gray-900">
                                      <span>🏛️</span>
                                      TXN_ID: {payout.payoutId}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}

                        {/* Payment Authorised Event */}
                        <div className="flex gap-6 pb-8 border-l-2 border-gray-300 pl-8 ml-6 relative">
                          <div className="absolute left-0 top-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center -ml-7 border-4 border-white">
                            <AlertCircle className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <div className="bg-white p-6 rounded-lg border border-gray-200">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-bold text-gray-900">Payment Authorised</h3>
                                <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded">FINANCE</span>
                              </div>
                              <p className="text-sm text-gray-600 mb-4">Budget allocation confirmed and batch payment signed for execution.</p>
                              <div className="flex items-center gap-3 text-xs text-gray-600">
                                <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
                                <span className="font-bold text-gray-900">{payoutData?.approved_by_name || payoutData?.authorised_by || 'Finance Team'}</span>
                                <span>{payout.calculatedDate || '—'}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Compliance Review Passed Event */}
                        <div className="flex gap-6 pb-8 border-l-2 border-gray-300 pl-8 ml-6 relative">
                          <div className="absolute left-0 top-0 w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center -ml-7 border-4 border-white">
                            <Check className="w-6 h-6 text-teal-600" />
                          </div>
                          <div className="flex-1">
                            <div className="bg-white p-6 rounded-lg border border-gray-200">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-bold text-gray-900">Compliance Review Passed</h3>
                                <span className="inline-block px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded">ADMIN</span>
                              </div>
                              <p className="text-sm text-gray-600 mb-4">KYC/AML verification cleared for all line items in this batch.</p>
                              <div className="flex items-center gap-3 text-xs text-gray-600">
                                <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
                                <span className="font-bold text-gray-900">{payoutData?.reviewed_by || 'Compliance Officer'}</span>
                                <span>{payout.calculatedDate || '—'}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Payout Generated Event */}
                        <div className="flex gap-6 pl-8 ml-6 relative">
                          <div className="absolute left-0 top-0 w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center -ml-7 border-4 border-white">
                            <span className="text-xl">📄</span>
                          </div>
                          <div className="flex-1">
                            <div className="bg-white p-6 rounded-lg border border-gray-200">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-bold text-gray-900">Payout Generated</h3>
                                <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs font-bold rounded">SYSTEM</span>
                              </div>
                              <p className="text-sm text-gray-600 mb-4">Automated job reconciliation completed. Payout draft established.</p>
                              <div className="text-xs text-gray-600">{payout.calculatedDate || '—'}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Other Tabs Content */}
                  {activeTab !== 'deductions' && activeTab !== 'ubbBreakdown' && (
                    <>
                      <div className="grid grid-cols-2 gap-6 mb-8">
                        <Card>
                          <CardHeader className="pb-4">
                            <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">Provider Details</p>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              <div>
                                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Legal Name</p>
                                <p className="font-bold text-gray-900">{payout.legalName}</p>
                              </div>
                              <div>
                                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Account Type</p>
                                <p className="font-bold text-gray-900">{payout.accountType}</p>
                              </div>
                              <div>
                                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Tax ID</p>
                                <p className="font-bold text-gray-900">{payout.taxId}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="pb-4">
                            <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">Gross/Net Calculation</p>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              <div className="border-b border-gray-200 pb-4">
                                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Total of UBB Orders</p>
                                <div className="flex items-baseline justify-between">
                                  <span className="text-gray-600">({payout.totalUrkOrders})</span>
                                  <span className="text-2xl font-bold text-gray-900">£{payout.totalUrkAmount.toFixed(2)}</span>
                                </div>
                              </div>

                              <div className="border-b border-gray-200 pb-4">
                                <p className="text-xs font-bold text-red-600 uppercase tracking-widest mb-2">Platform Fee ({payout.platformFeePercentage}%)</p>
                                <div className="flex items-baseline justify-between mb-1">
                                  <span className="text-xs text-gray-600">Standard After Tax</span>
                                  <span className="text-lg font-bold text-red-600">-${Math.abs(payout.totalFees).toFixed(2)}</span>
                                </div>
                              </div>

                              <div>
                                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Final Settlement</p>
                                <p className="text-3xl font-bold text-teal-600 mb-1">£{payout.netAmount.toFixed(2)}</p>
                                <p className="text-xs text-gray-600">Calculated on {payout.calculatedDate}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      <Card className="mb-8">
                        <CardHeader className="pb-4">
                          <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">Settlement Destination</p>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="w-10 h-10 bg-teal-600 rounded flex items-center justify-center text-white font-bold">🏦</div>
                            <div className="flex-1">
                              <p className="font-bold text-gray-900">{payout.bankAccount}</p>
                              <p className="text-sm text-gray-600">···· {payout.bankAccountLast4}</p>
                            </div>
                            {payout.isVerified && <div className="text-teal-600"><p className="text-sm font-bold">✓ Verified</p></div>}
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="pb-4">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-teal-600" />
                            <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">Audit Trail</p>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-gray-700">{payout.auditTrailInfo}</p>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </div>

                {/* Right Column */}
                <div className="col-span-1">
                  {/* Deductions Tab Sidebar */}
                  {activeTab === 'deductions' && (
                    <>
                      <Card className="mb-6 bg-gradient-to-br from-teal-600 to-teal-700 text-white border-none">
                        <CardContent className="pt-6">
                          <h3 className="text-lg font-bold mb-2">Automated Policy Enforcement</h3>
                          <p className="text-sm text-teal-100 leading-relaxed">System-wide deductions follow the Sovereign Compliance Framework v4.2. All manual adjustments require secondary peer review before final disbursement.</p>
                        </CardContent>
                      </Card>

                      <Card className="mb-6">
                        <CardContent className="pt-6">
                          <div className="space-y-6">
                            <div>
                              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3 flex items-center gap-2">
                                <div className="w-2 h-2 bg-teal-600 rounded-full"></div>
                                Decision Control
                              </p>
                              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                                <p className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-1">Approval Status</p>
                                <p className="text-sm font-bold text-gray-900">Ready for Disbursement</p>
                                <p className="text-xs text-gray-600 mt-2">All audit checks passed for batch {payout.payoutId}.</p>
                              </div>
                            </div>

                            <div>
                              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Internal Notes</p>
                              <textarea placeholder="Add compliance notes..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200 resize-none h-20 text-sm" />
                            </div>

                            <div>
                              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" />
                                Policy Note
                              </p>
                              <p className="text-xs text-gray-600 bg-gray-50 p-3 rounded-lg">Platform fees are non-refundable after disbursement confirmation.</p>
                            </div>

                            <div>
                              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">Audit Log</p>
                              <div className="space-y-2">
                                <div className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-teal-600 rounded-full mt-2 flex-shrink-0"></div>
                                  <div>
                                    <p className="text-sm font-bold text-gray-900">Deductions Finalized</p>
                                    <p className="text-xs text-gray-600">Today, 09:12 AM</p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                                  <div>
                                    <p className="text-sm text-gray-600">Adjustment Added</p>
                                    <p className="text-xs text-gray-600">Oct 14, 02:44 PM</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <div className="space-y-3">
                        <button
                          onClick={() => setShowReleasePayoutModal(true)}
                          className="w-full flex items-center gap-2 px-4 py-3 bg-teal-600 text-white font-bold rounded-lg hover:bg-teal-700 transition-colors justify-center"
                        >
                          <ArrowRight className="w-4 h-4" />
                          Release Funds
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm('Flag this payout for audit review?')) return;
                            try {
                              const res: any = await apiService.request(`/admin/payouts/${payoutId}/flag`, 'POST', {
                                reason: 'Audit flag from payout detail',
                                category: 'audit',
                              });
                              if (res?.success) alert('Payout flagged for audit');
                              else alert('Error: ' + (res?.message || 'Failed to flag'));
                            } catch (e: any) { alert('Failed: ' + (e?.message || 'Network error')); }
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-red-600 font-bold text-sm hover:bg-red-50 rounded-lg transition-colors justify-center"
                        >
                          <Flag className="w-4 h-4" />
                          Flag for Audit
                        </button>
                      </div>
                    </>
                  )}

                  {/* Job Breakdown Tab Sidebar */}
                  {activeTab === 'ubbBreakdown' && (
                    <>
                      <Card className="mb-6">
                        <CardContent className="pt-6">
                          <div className="space-y-6">
                            <div>
                              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3 flex items-center gap-2">
                                <div className="w-2 h-2 bg-teal-600 rounded-full"></div>
                                Decision Control
                              </p>
                              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                                <p className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-1">Approval Status</p>
                                <p className="text-sm font-bold text-gray-900">Awaiting Release</p>
                                <p className="text-xs text-gray-600 mt-2">Ready for final approval and disbursement.</p>
                              </div>
                            </div>

                            <div>
                              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Approver Notes</p>
                              <textarea placeholder="Optional audit trail notes..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200 resize-none h-24 text-sm" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <button className="w-full flex items-center gap-2 px-4 py-3 bg-teal-600 text-white font-bold rounded-lg hover:bg-teal-700 transition-colors justify-center mb-6">
                        <ArrowRight className="w-4 h-4" />
                        Release Payout
                      </button>

                      <Card className="mb-6">
                        <CardContent className="pt-6">
                          <div>
                            <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-4">Partial Release</p>
                            <div className="space-y-4">
                              <div>
                                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Provider Rating</p>
                                <div className="flex items-center gap-2">
                                  <div className="text-2xl font-bold text-gray-900">4.9<span className="text-sm text-gray-600">/5.0</span></div>
                                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div className="w-98% h-full bg-teal-500"></div>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600"><strong>"High trust score."</strong> No payed disputes in the last 12 months.</p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="mb-6 cursor-pointer hover:bg-gray-50 transition-colors">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Escalate Review</p>
                              <p className="text-sm text-gray-600">Flag to compliance officer</p>
                            </div>
                            <ArrowRight className="w-4 h-4 text-gray-400" />
                          </div>
                        </CardContent>
                      </Card>

                      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white rounded-lg p-6">
                        <p className="text-xs font-bold text-teal-100 uppercase tracking-widest mb-3">Compliance View</p>
                        <h3 className="text-lg font-bold text-white mb-1">Annual Certification</h3>
                        <p className="text-xs text-teal-100">Expires in 14 days</p>
                      </div>
                    </>
                  )}

                  {/* History Tab Sidebar */}
                  {activeTab === 'history' && (
                    <>
                      <Card className="mb-6">
                        <CardContent className="pt-6">
                          <div className="space-y-6">
                            <div>
                              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3 flex items-center gap-2">
                                <div className="w-2 h-2 bg-teal-600 rounded-full"></div>
                                Decision Control
                              </p>
                              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                                <p className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-3">Audit Status</p>
                                <p className="flex items-center gap-2 text-sm font-bold text-teal-700">
                                  <span className="w-2 h-2 bg-teal-600 rounded-full"></span>
                                  VERIFIED ARCHIVE
                                </p>
                              </div>
                            </div>

                            <div>
                              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Review Summary</p>
                              <p className="text-sm text-gray-700 italic">
                                "Final release approved based on automated compliance matching and human verification."
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <div className="space-y-3 mb-6">
                        <button
                          onClick={async () => {
                            if (!window.confirm('Reopen the audit on this payout?')) return;
                            try {
                              const res: any = await apiService.request(`/admin/payouts/${payoutId}/reopen-audit`, 'POST', {});
                              if (res?.success) alert('Audit reopened');
                              else alert('Error: ' + (res?.message || 'Failed to reopen'));
                            } catch (e: any) { alert('Failed: ' + (e?.message || 'Network error')); }
                          }}
                          className="w-full flex items-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors justify-center"
                        >
                          <span>↻</span>
                          Reopen Audit
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm('Flag this payout for finance review?')) return;
                            try {
                              const res: any = await apiService.request(`/admin/payouts/${payoutId}/flag`, 'POST', {
                                reason: 'Finance flag from payout detail',
                                category: 'finance',
                              });
                              if (res?.success) alert('Payout flagged for finance');
                              else alert('Error: ' + (res?.message || 'Failed to flag'));
                            } catch (e: any) { alert('Failed: ' + (e?.message || 'Network error')); }
                          }}
                          className="w-full flex items-center gap-2 px-4 py-3 bg-teal-600 text-white font-bold rounded-lg hover:bg-teal-700 transition-colors justify-center"
                        >
                          <Flag className="w-4 h-4" />
                          Flag for Finance
                        </button>
                      </div>

                      <Card className="bg-gray-800 text-white mb-6">
                        <CardContent className="pt-6">
                          <p className="text-xs font-bold text-gray-300 uppercase tracking-widest mb-4">Total Amount</p>
                          <p className="text-4xl font-bold text-white mb-4">£{payout.netAmount.toFixed(2)}</p>
                          <div className="grid grid-cols-2 gap-4 border-t border-gray-700 pt-4">
                            <div>
                              <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">Gross</p>
                              <p className="text-lg font-bold text-white">£{payout.grossTotal.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">Fees</p>
                              <p className="text-lg font-bold text-white">-${Math.abs(payout.totalFees).toFixed(2)}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="pt-6">
                          <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-4">Linked Entities</p>
                          <div className="space-y-3">
                            <div className="flex items-center gap-3">
                              <span className="text-lg">👤</span>
                              <div>
                                <p className="text-sm font-bold text-gray-900">{payout.providerName || '—'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-lg">📋</span>
                              <div>
                                <p className="text-sm font-bold text-gray-900">{payout.batchPeriod || '—'}</p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </>
                  )}

                  {/* Other Tabs Sidebar */}
                  {activeTab !== 'deductions' && activeTab !== 'ubbBreakdown' && activeTab !== 'history' && (
                    <>
                      <Card className="mb-6 border-yellow-200 bg-yellow-50">
                        <CardHeader className="pb-4">
                          <p className="text-xs font-bold text-yellow-800 uppercase tracking-widest">Place on Hold</p>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-bold text-gray-900 mb-2">Select Reason</label>
                              <select value={holdReason} onChange={(e) => setHoldReason(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-yellow-600 focus:ring-1 focus:ring-yellow-200 bg-white">
                                <option value="">Choose a reason...</option>
                                <option value="review">Incomplete Review</option>
                                <option value="compliance">Compliance Check</option>
                                <option value="dispute">Dispute Pending</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-bold text-gray-900 mb-2">Internal Notes</label>
                              <textarea value={holdNotes} onChange={(e) => setHoldNotes(e.target.value)} placeholder="Internal notes..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-yellow-600 focus:ring-1 focus:ring-yellow-200 resize-none h-24" />
                            </div>

                            <button
                              onClick={() => {
                                if (!holdReason) { alert('Please pick a hold reason'); return; }
                                handleHoldPayout();
                              }}
                              className="w-full px-4 py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors"
                            >
                              Apply Hold
                            </button>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="pt-6">
                          <div className="space-y-3">
                            <button className="w-full flex items-center gap-2 px-4 py-2 text-teal-600 font-bold text-sm hover:bg-teal-50 rounded-lg transition-colors justify-center">
                              <LinkIcon className="w-4 h-4" />
                              Link to Dispute #{payout.disputes[0]}
                            </button>
                            <button className="w-full flex items-center gap-2 px-4 py-2 text-red-600 font-bold text-sm hover:bg-red-50 rounded-lg transition-colors justify-center">
                              <Flag className="w-4 h-4" />
                              Flag for Fraud Review
                            </button>
                            <button className="w-full flex items-center gap-2 px-4 py-2 text-gray-600 font-bold text-sm hover:bg-gray-100 rounded-lg transition-colors justify-center border border-gray-200">
                              <X className="w-4 h-4" />
                              Close Panel
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Confirm Release Modal */}
          {showReleaseConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full mx-4">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-teal-600" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">Confirm Payout Release</h2>
                <p className="text-gray-600 text-center mb-8">
                  You are about to authorize a transfer of <span className="font-bold text-gray-900">£{payout.netAmount.toFixed(2)}</span> to <span className="font-bold text-gray-900">{payout.providerName}</span>. This action is irreversible.
                </p>
                <div className="flex gap-3 mb-6">
                  <button onClick={() => setShowReleaseConfirm(false)} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors">
                    Go Back
                  </button>
                  <button onClick={() => setShowReleaseConfirm(false)} className="flex-1 px-4 py-3 bg-teal-600 text-white font-bold rounded-lg hover:bg-teal-700 transition-colors">
                    Confirm Release
                  </button>
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-widest text-center">Transaction Security: AES-256 Encrypted</p>
                </div>
              </div>
            </div>
          )}

          {/* Release Payout Modal */}
          <ReleasePayoutModal
            providerName={payout.providerName}
            providerRole="Senior Technician"
            netPayout={`$${payout.netAmount.toFixed(2)} GBP`}
            relatedJobIds={Array.isArray((payout as any).relatedJobIds) ? (payout as any).relatedJobIds : []}
            paymentMethod="Direct Transfer"
            bankAccount={`Ending in •••• ${payout.bankAccountLast4}`}
            isOpen={showReleasePayoutModal}
            onClose={() => setShowReleasePayoutModal(false)}
            onConfirm={async () => {
              await handleReleasePayout();
              setShowReleasePayoutModal(false);
            }}
          />

          {/* Hold Payout Modal */}
          <HoldPayoutModal
            payoutId={payout.payoutId}
            payoutAmount={`$${payout.netAmount.toFixed(2)} GBP`}
            isOpen={showHoldPayoutModal}
            onClose={() => setShowHoldPayoutModal(false)}
            onConfirm={async (modalReason, internalNotes, _notifyProvider) => {
              setHoldReason(modalReason);
              setHoldNotes(internalNotes);
              // Defer one tick so state lands before request goes out.
              await new Promise(r => setTimeout(r, 0));
              await handleHoldPayout();
            }}
          />
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminPayoutDetailPage;
